import React from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button } from '@mui/material';
import { AssignmentTurnedIn as QuickActionsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const QuickActionsPage = () => {
  const navigate = useNavigate();

  const actionCategories = [
    {
      title: 'Student Management',
      actions: [
        { label: 'Add New Student', path: '/student-admissions', variant: 'contained', color: 'primary' },
        { label: 'View Students', path: '/student-directory', variant: 'outlined', color: 'secondary' },
        { label: 'Student Discipline', path: '/students-discipline', variant: 'outlined', color: 'warning' },
        { label: 'Attendance Summary', path: '/attendance-summary', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Staff Management',
      actions: [
        { label: 'Add New Staff', path: '/user-form', variant: 'contained', color: 'primary' },
        { label: 'View Staff List', path: '/user-list', variant: 'outlined', color: 'secondary' },
        { label: 'Staff Evaluation', path: '/staff/evaluations', variant: 'outlined', color: 'warning' },
        { label: 'Training & Development', path: '/staff/training', variant: 'outlined', color: 'success' },
        { label: 'Performance Reviews', path: '/performance-reviews', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Finance',
      actions: [
        { label: 'Process Payment', path: '/fee-payments', variant: 'contained', color: 'primary' },
        { label: 'Fees', path: '/fee-structure', variant: 'outlined', color: 'secondary' },
        { label: 'Financial Reports', path: '/financial-reports', variant: 'outlined', color: 'success' },
        { label: 'Salary Management', path: '/salary-management', variant: 'outlined', color: 'warning' }
      ]
    },
    {
      title: 'Academics',
      actions: [
        { label: 'Create Timetable', path: '/timetable', variant: 'contained', color: 'primary' },
        { label: 'Exam Management', path: '/exams', variant: 'outlined', color: 'warning' },
        { label: 'Grading Scales', path: '/grading-scales', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Teaching & Learning',
      actions: [
        { label: 'Student Grading', path: '/teacher/student-grading', variant: 'contained', color: 'primary' },
        { label: 'Attendance Recording', path: '/teacher/attendance-recording', variant: 'outlined', color: 'secondary' },
        // These carried a `/teacher` prefix that no route has ever answered to,
        // so both buttons went to the landing page. The real routes are
        // unprefixed — same fault as the "Enter marks" button, found by checking
        // every link in this portal against the routes that actually exist.
        { label: 'Class Management', path: '/class-management', variant: 'outlined', color: 'warning' },
        { label: 'Subject Management', path: '/subject-management', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Communication',
      actions: [
        { label: 'Send Announcement', path: '/communication/announcements', variant: 'contained', color: 'primary' },
        { label: 'Notifications', path: '/communication/notifications', variant: 'outlined', color: 'secondary' },
        { label: 'Emergency Alerts', path: '/communication/emergency-alerts', variant: 'outlined', color: 'error' },
        { label: 'Messages', path: '/messages', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Reports',
      actions: [
        { label: 'Academic Reports', path: '/reports/academic', variant: 'contained', color: 'primary' },
        { label: 'Financial Reports', path: '/reports/financial', variant: 'outlined', color: 'secondary' },
        { label: 'Operational Reports', path: '/reports/operational', variant: 'outlined', color: 'warning' },
        { label: 'Data Exporter', path: '/reports/data-exporter', variant: 'outlined', color: 'info' }
      ]
    },
    {
      title: 'Health & Facilities',
      actions: [
        { label: 'Health Records', path: '/health/records', variant: 'contained', color: 'success' },
        { label: 'Health Incidents', path: '/health/incidents', variant: 'outlined', color: 'error' },
        { label: 'Library Management', path: '/facilities/library-dashboard', variant: 'outlined', color: 'info' },
        { label: 'Transport Routes', path: '/facilities/transport-routes', variant: 'outlined', color: 'warning' }
      ]
    },
    {
      title: 'School Setup',
      actions: [
        { label: 'School Settings', path: '/school-setup-and-configuration/school-settings', variant: 'contained', color: 'primary' },
        { label: 'System Settings', path: '/school-setup-and-configuration/system-settings', variant: 'outlined', color: 'secondary' },
        { label: 'Role Permissions', path: '/role-permissions', variant: 'outlined', color: 'warning' },
        { label: 'Academic Sessions', path: '/school-setup-and-configuration/academic-sessions', variant: 'outlined', color: 'info' }
      ]
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <QuickActionsIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Quick Actions
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {actionCategories.map((category, categoryIndex) => (
          <Grid item xs={12} md={6} lg={4} key={categoryIndex}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {category.title}
                </Typography>
                {category.actions.map((action, actionIndex) => (
                  <Button 
                    key={actionIndex}
                    variant={action.variant} 
                    fullWidth 
                    sx={{ mb: 1 }}
                    color={action.color}
                    onClick={() => navigate(action.path)}
                  >
                    {action.label}
                  </Button>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default QuickActionsPage;
